import { useQuery } from "@tanstack/react-query";
import { privateApi } from "../../../api/api";
import { CountryReportResponseV4Schema } from "../response/CountryReportResponseV4";

const API_VERSION = 'v4';

// Tipe spesifik untuk kolom yang bisa diurutkan
type OrderByOption = "country" | "nb_reservations" | "amount";

// Antarmuka untuk parameter filter
interface FilterPropsV4 {
    property_id: string;
    start_date: string;
    end_date: string;
    base_on: "created_reservation" | "checkin_date";
    room_type_id?: string;
}

// Antarmuka untuk parameter hook
interface CountryReportV4Props {
    filter: FilterPropsV4;
    orderBy?: OrderByOption; // Menggunakan tipe yang sudah didefinisikan
    sort?: "asc" | "desc";
    type: string | null;
}

/**
 * Hook React Query untuk mengambil laporan negara v4.
 *
 * @param params - Filter, opsi pengurutan, dan tipe laporan.
 * @returns Hasil dari hook useQuery.
 */
const useCountryReportV4 = (params: CountryReportV4Props) => {
    return useQuery({
        queryKey: [
            'report',
            'country-report-v4',
            params.type,
            JSON.stringify(params.filter),
            params.orderBy,
            params.sort,
        ],
        queryFn: async () => {
            try {
                // Menyusun body request
                const requestBody = {
                    filter: params.filter,
                    orderBy: params.orderBy || "country", // orderBy default
                    sort: params.sort || "asc",         // sort default
                };

                const response = await privateApi.get(`/${API_VERSION}/channel-manager/property-report-country`, {
                    params: requestBody
                });

                // Validasi respons dengan skema Zod
                const validationResult = CountryReportResponseV4Schema.safeParse(response.data);
                if (!validationResult.success) {
                    console.error("Validation failed:", validationResult.error.errors);
                    throw new Error("Invalid V4 Country report data format");
                }
                return validationResult.data;
            } catch (error) {
                console.error(`Failed to fetch V4 Country report`, error);
                throw error;
            }
        },
        enabled: !!params.filter && !!params.filter.property_id && !!params.filter.start_date && !!params.filter.end_date && params.type === "country_report",
        retry: 1,
        refetchOnWindowFocus: false,
    });
};

export default useCountryReportV4;